<template>
  <div>
    <nav class="woocommerce-breadcrumb" itemprop="breadcrumb">
      <a href="/dashboard">Dashboard</a> / <span>Sync Management</span>
    </nav>
    <div class="container-fluid sync-management-page">
      <div v-if="notification.text" class="sync-management-page-notification">
        <span v-html="notification.icon"></span>
        <span>{{ notification.text }}</span>
        <button @click="notification = {}" class="sync-management-page-notification__close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <h1 class="sync-management-page__heading">
        Sync Management
        <a
          class="btn btn-lg btn-outline-primary sync-management-page__learn"
          href="https://intercom.help/snappy-kraken/en/"
          target="_blank"
        >
          <i class="fas fa-video"></i>Learn</a
        >
      </h1>

      <template v-if="crm">
        <div class="sync-management-page-details">
          <img
            :src="imageUrl"
            :alt="displayName"
            class="sync-management-page-details__logo"
          />
          <div class="sync-management-page-details__status">
            <span class="sync-management-page-details__crmname">
              {{ displayName }} CRM
              <snappy-badge background="green">Connected</snappy-badge>
            </span>
            <span v-if="connected" class="sync-management-page-details__time"
              >Account connected on: {{ connected }}</span
            >
          </div>
          <div class="sync-management-page-details__sync">
            <template v-if="lastSynced">
              <span class="sync-management-page-details__lastsync"> Last Synced </span>
              <span class="sync-management-page-details__time">{{ lastSynced }}</span>
            </template>
          </div>
          <div class="sync-management-page-details__actions">
            <a href="/sync/select-groups" class="btn btn-lg btn-primary">Sync</a>
          </div>
        </div>
        <div class="sync-management-page-history">
          <a href="/sync/management" class="sync-management-page-history__reload">
            <i class="fas fa-sync-alt"></i>
          </a>
          <span class="sync-management-page-history__title">Sync History</span>
          <snappy-dropdown text="Sort by:" inverted :items="actions">
            <template v-slot:trigger><strong>Sort by: </strong>{{ sort }} </template>
          </snappy-dropdown>
        </div>
        <sync-management-report v-for="report in reports" :key="report.id" :report="report"></sync-management-report>
      </template>
      <sync-management-no-crm v-else></sync-management-no-crm>
    </div>
  </div>
</template>

<script>
import SyncManagementNoCrm from '../components/sync/SyncManagementNoCrm.vue';
import SyncManagementReport from '../components/sync/SyncManagementReport.vue';
import SnappyBadge from '../components/SnappyBadge.vue';
import SnappyDropdown from '../components/SnappyDropdown.vue';
import {getCommercialName} from '../utility/crms/getCommercialName.js';
import dateFormatter from '../mixins/dateFormatter';
import {getQueryParamsMap} from '../utility/router/getQueryParamsMap';
import {listenToPusherSyncEvents} from '../utility/notifications/listenToPusherSyncEvents';
import swal from 'sweetalert2';
import axios from 'axios';

export default {
  name: 'sync-management-page',
  mixins: [dateFormatter],
  components: {SyncManagementNoCrm, SnappyBadge, SnappyDropdown, SyncManagementReport},
  data() {
    return {
      actions: [
        {text: 'Last synced', href: '?sort=desc'},
        {text: 'First synced', href: '?sort=asc'},
      ],
      notification: {},
      progress: 0,
      contacts: 0,
      groups: 0,
      paramsMap: null,
      timeout: null,
    };
  },
  props: {
    crm: {
      type: String,
    },
    availability: {
      type: String,
    },
    syncReports: {
      type: String,
    },
    timezone: {
      type: String,
    },
  },
  mounted() {
    this.paramsMap = getQueryParamsMap();
    this.initializePusherSyncEvents();
  },
  methods: {
    showNotification(status, text) {
      let icon = '';
      if (status === 'progress') {
        icon = '<i class="fas fa-sync-alt  sync-management-page-notification__progress"></i>';
      }
      if (status === 'success') {
        icon = '<i class="fas fa-check  sync-management-page-notification__success"></i>';
      }
      if (status === 'error') {
        icon = '<i class="fas fa-exclamation  sync-management-page-notification__error"></i>';
      }
      this.notification = Object.assign({}, this.notification, {text, icon});
    },
    initializePusherSyncEvents() {
      listenToPusherSyncEvents(notification => {
        clearTimeout(this.timeout);
        const {groups, contacts_received, contacts_processed} = notification.message.counts;
        this.groups = groups;
        this.contacts = contacts_received;
        this.progress =
          contacts_processed > this.progress
            ? Math.trunc((contacts_processed / contacts_received) * 100)
            : this.progress;
        this.showNotification('progress', `Your contacts are currently syncing — ${this.progress}% complete.`);

        if (notification.message.type === 'sync-finished') {
          this.progress = 100;
          this.showNotification('progress', `Your contacts are currently syncing — ${this.progress}% complete.`);
          setTimeout(() => {
            this.showNotification('success', `Success! Your ${this.crmName} account has been synced.`);
          }, 500);
        }
        if (notification.message.type === 'sync-failed') {
          this.showNotification('error', 'Error! Your sync failed.');
        }
        this.timeout = setTimeout(() => {
          this.notification = {};
        }, 5000);
      });
    },
  },
  computed: {
    sort() {
      return this.paramsMap?.sort === 'asc' ? 'First synced' : 'Last Synced';
    },
    activeCrm() {
      return JSON.parse(this.crm);
    },
    reports() {
      return JSON.parse(this.syncReports);
    },
    lastSynced() {
      if (!this.activeCrm.last_sync_at) return;
      return this.dateLarge(new Date(this.activeCrm.last_sync_at), this.timezone);
    },
    connected() {
      if (!this.activeCrm.created_at) return;
      return this.dateLarge(new Date(this.activeCrm.created_at), this.timezone);
    },
    crmName() {
      return getCommercialName(JSON.parse(this.crm).crm_name);
    },
    available() {
      return JSON.parse(this.availability);
    },
    displayName() {
      return this.crmName;
    },
    imageUrl() {
      return `/wp-content/themes/snappy-theme/assets/images/crms/${this.activeCrm.crm_name}.svg`;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.sync-management-page {
  min-height: calc(100vh - 74px);
  text-align: left;
  padding: 16px;
  position: relative;
  @media (min-width: $breakpoint-sm) {
    padding: 24px;
  }
  &-notification {
    position: absolute;
    top: 10px;
    right: 30px;
    height: 48px;
    background: $white-color;
    display: flex;
    align-items: center;
    padding: 0 16px;
    box-shadow: 2px 2px 4px rgba(33, 33, 33, 0.08);
    border: 1px solid transparent;
    border-radius: 4px;
    z-index: 9999;
    transition: all 0.3s;
    &:hover {
      border: 1px solid $half-baked-color;
    }
    &__close {
      padding: 0;
      background: transparent;
      color: currentColor;
      margin-left: 15px;
    }
  }
  &__icon--progress {
    color: $half-baked-color;
    font-size: 20px;
  }
  &__heading {
    font-size: 48px;
    min-height: 56px;
    visibility: visible;
    margin-bottom: 0;

    @media (min-width: $breakpoint-sm) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
  &__learn {
    display: inline-flex;
    gap: 10px;
    font-weight: bold;
    align-items: center;
    @media (min-width: $breakpoint-sm) {
      display: flex;
    }
  }
  &-card {
    width: 100%;
    background: $white-color;
    min-height: 104px;
  }

  &-details {
    background: $white-color;
    gap: 15px;
    border: 1px solid $platinum-color;
    border-radius: 4px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    min-height: 104px;
    padding: 16px;
    @media (min-width: $breakpoint-md) {
      display: grid;
      grid-template-columns: max-content minmax(max-content, 350px) 1fr max-content;
      grid-template-rows: 1fr;
    }
    &__logo {
      height: 72px;
      align-self: center;
    }
    &__status {
      align-self: center;
    }
    &__crmname {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 32px;
      font-weight: 700;
      color: $mine-shaft-color;
    }
    &__time {
      white-space: nowrap;
      display: block;
    }
    &__sync {
      align-self: center;
    }
    &__lastsync {
      font-size: 20px;
      font-weight: 700;
      color: $mine-shaft-color;
      line-height: 50px;
    }
    &__actions {
      align-self: center;
      align-items: center;
      display: flex;
      gap: 20px;
    }
    &__disconnect {
      font-weight: 700;
      color: $cyan-color;
    }
  }
  &-history {
    background: $white-color;
    min-height: 80px;
    padding: 16px;
    border: 1px solid $platinum-color;
    @media (min-width: $breakpoint-sm) {
      display: grid;
      grid-template-columns: 48px 1fr min-content;
      grid-template-rows: 1fr;
      gap: 20px;
    }
    &__title {
      font-weight: 700;
      font-size: 24px;
      display: inline-flex;
      align-items: center;
      color: $mine-shaft-color;
    }
    &__reload {
      height: 48px;
      width: 48px;
      background: $mine-shaft-color;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      color: $white-color;
      font-size: 24px;
    }
    &__sortby {
      color: $mine-shaft-color;
    }
  }
}
</style>

<style lang="scss">
@import '../../sass/web/_variables.scss';
.sync-management-page-notification {
  &__progress {
    color: $half-baked-color;
    animation: rotating 1.5s linear infinite;
    margin-right: 10px;
  }
  &__success,
  &__error {
    margin-right: 10px;
    width: 21px;
    height: 21px;
    color: $white-color;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
  &__success {
    background: $half-baked-color;
  }
  &__error {
    background: $deep-carmine-pink-color;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
